<p class="text-sm font-medium text-gray-900 mt-4">Policy Details</p>
<p class="text-sm text-gray-600 mb-4">Add a name, short description, number, and waiting period for this policy.</p>

<form *vsLet="patchValue$ | async" class="flex flex-col gap-4" [formGroup]="form">
  <vs-form-field>
    <div class="flex gap-4 items-center">
      <label for="title-input" class="text-sm font-medium text-gray-700 basis-1/5">Title</label>
      <input vsInput id="title-input" type="text" formControlName="title" placeholder="Enter the policy title" />
    </div>
  </vs-form-field>

  <vs-form-field>
    <div class="flex gap-4 items-center">
      <label for="friendly-title-input" class="text-sm font-medium text-gray-700 basis-1/5">
        Friendly Title
        <vs-icon
          name="info-circle"
          tooltipStyle="light"
          class="text-base !text-gray-900 self-center -mb-0.5"
          [vsTooltip]="{
            title: 'Friendly Title',
            subtitle:
              'This title will be displayed to customers and distributors when selecting a policy at checkout. We recommend a short title that explains what type of asset this policy would cover.',
          }"
        ></vs-icon>
      </label>

      <input
        vsInput
        id="friendly-title-input"
        type="text"
        formControlName="friendlyTitle"
        placeholder="Enter the policy's friendly title"
      />
    </div>

    <div class="flex w-full justify-end">
      <span
        *ngIf="form.value.friendlyTitle && form.value.friendlyTitle.length > 50"
        slot="hint"
        class="text-danger text-right text-sm"
      >
        {{ form.value.friendlyTitle.length }} / 50
      </span>
    </div>
  </vs-form-field>

  <vs-form-field>
    <div class="flex gap-4 items-center">
      <label for="tagline-input" class="text-sm font-medium text-gray-700 basis-1/5">Tagline</label>
      <input
        vsInput
        id="tagline-input"
        type="text"
        formControlName="tagline"
        placeholder="Enter the policy's short description"
      />
    </div>

    <div class="flex w-full justify-end">
      <span
        *ngIf="form.value.tagline && form.value.tagline.length > 100"
        slot="hint"
        class="text-danger text-right text-sm"
      >
        {{ form.value.tagline.length }} / 100
      </span>
    </div>
  </vs-form-field>

  <vs-form-field>
    <div class="flex items-start">
      <label for="description-input" class="text-sm font-medium text-gray-700 basis-1/5 pt-3">Description</label>
      <div class="flex-1 min-w-0">
        <vs-rich-text-editor
          id="description-input"
          formControlName="description"
          placeholder="Enter the policy's description"
        ></vs-rich-text-editor>
      </div>
    </div>
  </vs-form-field>

  <vs-form-field *ngIf="editing">
    <div class="flex gap-4 items-center">
      <label for="order-input" class="text-sm font-medium text-gray-700 basis-1/5">Order</label>
      <input
        vsInput
        id="order-input"
        type="number"
        formControlName="order"
        min="0"
        placeholder="Enter the display order"
      />
    </div>
  </vs-form-field>

  <vs-form-field>
    <div class="flex gap-4 items-center">
      <label for="carrier-input" class="text-sm font-medium text-gray-700 basis-1/5">Carrier</label>
      <input
        vsInput
        id="carrier-input"
        type="text"
        formControlName="carrier"
        placeholder="Enter the insurance carrier"
        maxlength="50"
      />
    </div>
  </vs-form-field>

  <vs-form-field>
    <div class="flex gap-4 items-center">
      <label for="tags-input" class="text-sm font-medium text-gray-700 basis-1/5">Tags</label>
      <input vsInput id="tags-input" type="text" formControlName="tags" placeholder="tag1,tag2" />
    </div>
    <span *ngIf="form.get('tags')?.invalid" slot="hint" class="text-danger text-left text-sm">
      Tags must be alphanumeric and comma-separated (spaces after commas are optional).
    </span>
  </vs-form-field>

  <vs-form-field>
    <label class="flex gap-1 text-sm font-medium text-gray-700 basis-1/5">
      Icon
      <vs-icon
        name="info-circle"
        tooltipStyle="light"
        class="text-base !text-gray-900 self-center"
        [vsTooltip]="{
          title: 'Icon',
          subtitle: 'This icon will be displayed to customers and distributors when selecting a policy at checkout.',
        }"
      ></vs-icon>
    </label>

    <vs-icon-picker formControlName="icon"></vs-icon-picker>
  </vs-form-field>

  <vs-form-field *ngIf="!editing || (prefixEditable$ | async)">
    <div class="flex gap-4 items-center">
      <label for="prefix-input" class="text-sm font-medium text-gray-700 basis-1/5">Prefix</label>
      <input
        class="basis-2/6 ml-2"
        vsInput
        id="prefix-input"
        type="text"
        formControlName="prefix"
        [vsTooltip]="
          editing
            ? 'This policy has no warranties yet, so its prefix can still be changed.'
            : 'Once you set this policy\'s prefix, it cannot be adjusted.'
        "
        tooltipStyle="light"
        placeholder="ABC"
      />

      <label for="number-input" class="text-sm font-medium text-gray-700 basis-1/5">Number</label>
      <input vsInput id="number-input" type="text" class="basis-2/6" formControlName="policyNumber" readonly="true" />
    </div>
    <span
      *ngIf="form.value.prefix && form.get('prefix')?.errors?.['pattern']"
      slot="hint"
      class="text-danger text-left text-sm"
    >
      Prefix must only contain letters
    </span>
  </vs-form-field>

  <vs-form-field>
    <div class="flex flex-row">
      <div class="flex gap-1 self-center basis-1/2">
        <label class="text-sm font-medium text-gray-700" for="claim-input">Claim Waiting Period</label>

        <vs-icon
          class="self-center"
          name="info-circle"
          tooltipStyle="light"
          [vsTooltip]="{ template: waitingPeriodTooltipTemplate }"
        ></vs-icon>
      </div>

      <vs-input-container class="basis-1/2">
        <input id="claim-input" placeholder="Days" type="number" min="0" formControlName="claimWaitingPeriod" />
        <span slot="end">Days</span>
      </vs-input-container>
    </div>
  </vs-form-field>

  <vs-form-field>
    <div class="flex items-center gap-2">
      <input
        vsInput
        id="activation"
        type="checkbox"
        name="activation"
        formControlName="requiresWarrantyActivation"
        class="cursor-pointer"
      />
      <label class="text-sm font-medium text-gray-700 cursor-pointer" vsCheckboxLabel for="activation">
        {{ config.activation.label }}
      </label>
    </div>
    <p class="ml-6 text-gray-700 text-sm">
      {{ config.activation.description }}
    </p>
  </vs-form-field>

  <vs-form-field>
    <div class="flex items-center gap-2">
      <input
        vsInput
        id="allow-markup"
        type="checkbox"
        name="allow-markup"
        formControlName="allowMarkup"
        class="cursor-pointer"
      />
      <label class="text-sm font-medium text-gray-700 cursor-pointer" vsCheckboxLabel for="allow-markup">
        {{ config.allowMarkup.label }}
      </label>
    </div>
    <p class="ml-6 text-gray-700 text-sm">
      {{ config.allowMarkup.description }}
    </p>
  </vs-form-field>

  <vs-form-field>
    <div class="flex items-center gap-2">
      <input
        vsInput
        id="check-claim-eligibility"
        type="checkbox"
        name="check-claim-eligibility"
        formControlName="checkClaimEligibility"
        class="cursor-pointer"
      />
      <label class="text-sm font-medium text-gray-700 cursor-pointer" vsCheckboxLabel for="check-claim-eligibility">
        {{ config.checkClaimEligibility.label }}
      </label>
    </div>
    <p class="ml-6 text-gray-700 text-sm">
      {{ config.checkClaimEligibility.description }}
    </p>
  </vs-form-field>

  <vs-form-field>
    <div class="flex gap-4 items-center">
      <label for="checkout-plan-display" class="flex gap-1 text-sm font-medium text-gray-700 basis-1/5">
        Checkout Plan Display
        <vs-icon
          name="info-circle"
          tooltipStyle="light"
          class="text-base !text-gray-900 self-center"
          [vsTooltip]="{
            title: 'Checkout Plan Display',
            subtitle:
              'Controls the order of selection in checkout. Term → Plan shows the term selector first; Plan → Term shows the plan selector first (useful when each plan has a different set of terms).',
          }"
        ></vs-icon>
      </label>

      <vs-select
        vsInput
        id="checkout-plan-display"
        class="flex-1"
        formControlName="checkoutPlanDisplay"
        [config]="{ placeholder: 'Select how plans and terms are displayed' }"
      >
        <li *ngFor="let option of checkoutPlanDisplayOptions" vs-select-option [value]="option.value">
          {{ option.label }}
        </li>
      </vs-select>
    </div>
  </vs-form-field>
</form>

<!-- Buttons -->
<div
  class="flex gap-2 flex-wrap sm:flex-row mt-4"
  [class]="editing && policy?.status === draft ? 'justify-end' : 'justify-between'"
>
  <div
    *ngIf="!editing"
    class="flex gap-2 self-center flex-grow sm:flex-grow-0 -mb-14 max-w-full sm:max-w-max w-full sm:w-max"
  >
    <div class="self-center rounded-lg h-2 bg-primary-500 flex-grow sm:flex-grow-0 w-full sm:w-10"></div>
    <div class="self-center rounded-lg h-2 bg-gray-100 flex-grow sm:flex-grow-0 w-full sm:w-10"></div>
    <div class="self-center rounded-lg h-2 bg-gray-100 flex-grow sm:flex-grow-0 w-full sm:w-10"></div>
    <div class="self-center rounded-lg h-2 bg-gray-100 flex-grow sm:flex-grow-0 w-full sm:w-10"></div>
    <div class="self-center rounded-lg h-2 bg-gray-100 flex-grow sm:flex-grow-0 w-full sm:w-10"></div>
  </div>

  <div *ngIf="editing && policy?.status !== draft" class="flex gap-2 text-sm text-danger-500">
    <vs-icon class="self-center" name="alert-circle" size="md"></vs-icon>
    <span class="self-center"> Saving will create a new policy version, the current policy will not be modified </span>
  </div>

  <div class="flex gap-2 justify-end self-center w-full mt-2 sm:mt-0 flex-grow sm:flex-grow-0">
    <button vs-button appearance="outline" class="flex-grow sm:flex-grow-0" [class.flex-1]="editing" (click)="close()">
      Cancel
    </button>

    <button
      vs-button
      size="md"
      theme="primary"
      class="flex-grow sm:flex-grow-0"
      [class.flex-1]="editing"
      [disabled]="saving || form.invalid || !form.dirty"
      (click)="editing ? save() : pageComplete()"
    >
      {{ editing ? 'Save' : 'Next' }}
    </button>
  </div>
</div>

<ng-template #waitingPeriodTooltipTemplate>
  <div class="max-w-[240px]">
    <p class="text-gray-700 text-xs font-semibold pb-1">Claim waiting period</p>
    <p class="text-gray-500 text-xs font-medium">
      This is the number of days a customer must wait before filing a claim on their newly purchased warranty.
    </p>
  </div>
</ng-template>
